import type { PodcastShowData } from '../PodcastsElement.types';

interface PodcastCardProps {
  data: PodcastShowData;
  translations: Record<string, string>;
  href?: string;
  onClick?: () => void;
}

export function PodcastCard({ data, translations, href, onClick }: PodcastCardProps) {
  const As = data.isActive ? 'a' : 'div';
  const isExternalHref = Boolean(href?.startsWith('http'));

  return (
    <As
      href={data.isActive ? href : undefined}
      onClick={data.isActive ? onClick : undefined}
      target={data.isActive && isExternalHref ? '_blank' : undefined}
      rel={data.isActive && isExternalHref ? 'noreferrer' : undefined}
      className={`block border border-gray-200 rounded-lg p-4 ${
        data.isActive ? 'hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer' : 'opacity-75'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Podcast Artwork */}
        <div className="flex-shrink-0 w-20 h-20 md:w-[88px] md:h-[88px] overflow-hidden bg-gray-100 rounded-lg">
          {data.artwork_asset_url ? (
            <img src={data.artwork_asset_url} alt={data.title} className="object-cover w-full h-full" />
          ) : (
            <div className="flex items-center w-20 h-20 bg-gray-200 rounded-lg">
              <span className="text-3xl">🎙️</span>
            </div>
          )}
        </div>

        <div className="flex flex-col items-start self-center flex-1 min-w-0 gap-1">
          {data.is_premium && (
            <div className="block text-xs font-medium text-indigo-500">
              {translations?.['manage.podcasts.premium_badge'] || 'PREMIUM'}
            </div>
          )}
          <h2 className="text-lg font-medium text-gray-900 line-clamp-2 lg:line-clamp-1">{data.title}</h2>
          {data.description && (
            <div className="hidden lg:block ">
              <p className="text-sm font-normal text-gray-700 line-clamp-1">{data.description}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 mt-3">
        {data.description && (
          <div className="lg:hidden">
            <p className="text-sm font-normal text-gray-700 line-clamp-2">{data.description}</p>
          </div>
        )}
        {data.isActive && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--pt-background-color)]">
              {translations?.['manage.podcasts.setup_app'] || 'Set up podcast app'}
              <span>→</span>
            </span>
          </div>
        )}
        {!data.isActive && (
          <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {translations?.['manage.podcasts.status.inactive'] || 'Subscription inactive'}
          </div>
        )}
      </div>
    </As>
  );
}
